import * as Sentry from '@sentry/browser';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateCmsStateAndDispatch } from 'src/services/cms/stateUtils';
import { CmsActionTypes, CmsState, Status } from 'src/services/cms/typesUtil';

/**
 * Hook to get CMS-agnostic data for a content type
 * @param getData Function that connects to the CMS, returns a promise resolving to object of type CMSState containing decoupled data for Content type "T"
 * @returns CMS agnostic data for the content type passed into the type variable "T", or null
 */
export function useCMS<T>(getData: (s: string) => Promise<CmsState<T>>): T | undefined {
  const { language } = useTranslation().i18n;
  const { state, dispatch } = generateCmsStateAndDispatch<T>();

  const getCMSData = useCallback(async (language: string) => {
    dispatch({ type: CmsActionTypes.FETCH });

    const { status, data, message } = await getData(language);

    switch (status) {
      case Status.ERRORED:
        dispatch({ type: CmsActionTypes.ERROR, payload: message });
        const errMessage = `Could not get CMS data: ${message}.`;
        console.error(errMessage);
        Sentry.captureException(new Error(errMessage));
        break;
      case Status.RESOLVED:
        if (!data) return;
        dispatch({ type: CmsActionTypes.SUCCESS, payload: data });
        break;
      default:
        return;
    }
  }, []);

  useEffect(() => {
    getCMSData(language);
  }, [getCMSData, language]);

  return state.data;
}
